/**
 * Shared focused creative catalogs for porch + café production packs.
 * Labels live here (pack content) — not in shell UI string tables.
 */
import type {
  CameraMotionPreset,
  HeadlineTemplate,
  LookTreatment,
  SfxOption,
  StickerOption,
  TransitionPreset,
} from "@desk/core";

/** Wired production SFX asset IDs (shutter/crowd omitted — see licenses/audio/README.md). */
export const WIRED_SFX_ASSET_IDS = [
  "sfx-whoosh",
  "sfx-impact",
  "sfx-notify-positive",
  "sfx-notify-alert",
  "sfx-glitch",
  "sfx-success",
] as const;

export const SHARED_LOOK_TREATMENTS: LookTreatment[] = [
  {
    id: "look-clean",
    label: "Clean",
    tags: ["clean", "neutral"],
    contrastMilli: 1000,
    saturationMilli: 1000,
    warmthMilli: 1000,
    vignetteMilli: 0,
    intensitySupported: false,
    defaultIntensityMilli: 1000,
    presentationReachMilli: 0,
    productionMilli: 10,
  },
  {
    id: "look-warm-glow",
    label: "Warm Glow",
    tags: ["warm", "expressive"],
    contrastMilli: 1050,
    saturationMilli: 1120,
    warmthMilli: 1180,
    vignetteMilli: 120,
    intensitySupported: true,
    defaultIntensityMilli: 700,
    presentationReachMilli: 0,
    productionMilli: 35,
  },
  {
    id: "look-cool-cast",
    label: "Cool Cast",
    tags: ["cool", "expressive"],
    contrastMilli: 1100,
    saturationMilli: 900,
    warmthMilli: 820,
    vignetteMilli: 80,
    intensitySupported: true,
    defaultIntensityMilli: 650,
    presentationReachMilli: 0,
    productionMilli: 30,
  },
  {
    id: "look-high-contrast",
    label: "Punchy",
    tags: ["contrast", "expressive", "urgent"],
    contrastMilli: 1280,
    saturationMilli: 1080,
    warmthMilli: 1000,
    vignetteMilli: 200,
    intensitySupported: true,
    defaultIntensityMilli: 800,
    presentationReachMilli: 0,
    productionMilli: 45,
  },
  {
    id: "look-soft-fade",
    label: "Soft Fade",
    tags: ["soft", "expressive"],
    contrastMilli: 900,
    saturationMilli: 880,
    warmthMilli: 1040,
    vignetteMilli: 280,
    intensitySupported: true,
    defaultIntensityMilli: 600,
    presentationReachMilli: 0,
    productionMilli: 40,
  },
  {
    id: "look-noir",
    label: "Noir",
    tags: ["noir", "mono", "dramatic", "expressive"],
    contrastMilli: 1320,
    saturationMilli: 240,
    warmthMilli: 980,
    vignetteMilli: 360,
    intensitySupported: true,
    defaultIntensityMilli: 800,
    presentationReachMilli: 0,
    productionMilli: 42,
  },
  {
    id: "look-golden-hour",
    label: "Golden Hour",
    tags: ["warm", "golden", "expressive"],
    contrastMilli: 1060,
    saturationMilli: 1160,
    warmthMilli: 1320,
    vignetteMilli: 160,
    intensitySupported: true,
    defaultIntensityMilli: 720,
    presentationReachMilli: 0,
    productionMilli: 38,
  },
  {
    id: "look-vhs",
    label: "VHS",
    tags: ["retro", "vhs", "expressive"],
    contrastMilli: 940,
    saturationMilli: 1240,
    warmthMilli: 940,
    vignetteMilli: 240,
    intensitySupported: true,
    defaultIntensityMilli: 650,
    presentationReachMilli: 0,
    productionMilli: 44,
  },
  {
    id: "look-vibrant",
    label: "Vibrant",
    tags: ["vibrant", "punchy", "expressive"],
    contrastMilli: 1160,
    saturationMilli: 1420,
    warmthMilli: 1020,
    vignetteMilli: 40,
    intensitySupported: true,
    defaultIntensityMilli: 760,
    presentationReachMilli: 0,
    productionMilli: 36,
  },
  {
    id: "look-faded",
    label: "Faded",
    tags: ["faded", "soft", "expressive"],
    contrastMilli: 840,
    saturationMilli: 760,
    warmthMilli: 1080,
    vignetteMilli: 140,
    intensitySupported: true,
    defaultIntensityMilli: 560,
    presentationReachMilli: 0,
    productionMilli: 34,
  },
  {
    id: "look-neon",
    label: "Neon",
    tags: ["neon", "cool", "expressive", "urgent"],
    contrastMilli: 1260,
    saturationMilli: 1360,
    warmthMilli: 880,
    vignetteMilli: 200,
    intensitySupported: true,
    defaultIntensityMilli: 780,
    presentationReachMilli: 0,
    productionMilli: 46,
  },
  // ── Advanced editor FX looks ────────────────────────────────────────────────
  // These reuse the existing `look` op kind + grade knobs for scoring/validation,
  // but the shell recognizes their ids/tags (see creative-fx.ts) to render a live
  // animated overlay (rain / lightning / radial focus blur) in the editor preview
  // and a static approximation on the exported result card. Grade knobs stay near
  // neutral so the atmospheric overlay carries the effect. Cosmetic (reach 0).
  {
    id: "look-focus-blur",
    label: "Focus Blur",
    tags: ["focus", "cinematic", "expressive"],
    contrastMilli: 1030,
    saturationMilli: 1020,
    warmthMilli: 1000,
    vignetteMilli: 60,
    intensitySupported: false,
    defaultIntensityMilli: 1000,
    presentationReachMilli: 0,
    productionMilli: 42,
  },
  {
    id: "look-rain",
    label: "Rain",
    tags: ["rain", "moody", "cool", "expressive"],
    contrastMilli: 1050,
    saturationMilli: 900,
    warmthMilli: 910,
    vignetteMilli: 160,
    intensitySupported: true,
    defaultIntensityMilli: 700,
    presentationReachMilli: 0,
    productionMilli: 44,
  },
  {
    id: "look-lightning",
    label: "Lightning",
    tags: ["lightning", "storm", "dramatic", "urgent", "expressive"],
    contrastMilli: 1160,
    saturationMilli: 940,
    warmthMilli: 940,
    vignetteMilli: 220,
    intensitySupported: true,
    defaultIntensityMilli: 760,
    presentationReachMilli: 0,
    productionMilli: 46,
  },
];

export const SHARED_CAMERA_MOTIONS: CameraMotionPreset[] = [
  {
    id: "motion-steady",
    label: "Steady",
    tags: ["steady"],
    amplitudeMilli: 0,
    frequencyMilli: 0,
    zoomDeltaMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 5,
  },
  {
    id: "motion-push-in",
    label: "Push In",
    tags: ["push", "dramatic"],
    amplitudeMilli: 120,
    frequencyMilli: 200,
    zoomDeltaMilli: 180,
    presentationReachMilli: 0,
    productionMilli: 30,
  },
  {
    id: "motion-handheld",
    label: "Handheld",
    tags: ["handheld", "nervous"],
    amplitudeMilli: 220,
    frequencyMilli: 900,
    zoomDeltaMilli: 40,
    presentationReachMilli: 0,
    productionMilli: 35,
  },
  {
    id: "motion-snap-zoom",
    label: "Snap Zoom",
    tags: ["snap", "urgent"],
    amplitudeMilli: 80,
    frequencyMilli: 1400,
    zoomDeltaMilli: 280,
    presentationReachMilli: 0,
    productionMilli: 40,
  },
];

export const SHARED_TRANSITION_PRESETS: TransitionPreset[] = [
  {
    id: "transition-hard-cut",
    label: "Hard Cut",
    tags: ["cut", "clean"],
    maxDurationTicks: 1,
    defaultDurationTicks: 1,
    presentationReachMilli: 0,
    productionMilli: 5,
  },
  {
    id: "transition-flash",
    label: "Flash",
    tags: ["flash", "urgent"],
    maxDurationTicks: 8,
    defaultDurationTicks: 4,
    presentationReachMilli: 0,
    productionMilli: 35,
  },
  {
    id: "transition-swipe",
    label: "Swipe",
    tags: ["swipe", "kinetic"],
    maxDurationTicks: 12,
    defaultDurationTicks: 6,
    presentationReachMilli: 0,
    productionMilli: 30,
  },
];

export const SHARED_STICKER_OPTIONS: StickerOption[] = [
  {
    id: "sticker-wow",
    label: "WOW",
    tags: ["text", "hype"],
    render: { kind: "procedural", shape: "text", text: "WOW", colorTag: "coral" },
    defaultScaleMilli: 1100,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 25,
  },
  {
    id: "sticker-wait",
    label: "Wait…",
    tags: ["text", "suspense"],
    render: { kind: "procedural", shape: "badge", text: "WAIT", colorTag: "cyan" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 20,
  },
  {
    id: "sticker-arrow",
    label: "Look here",
    tags: ["arrow", "direct"],
    render: { kind: "procedural", shape: "arrow", colorTag: "coral" },
    defaultScaleMilli: 900,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 22,
  },
  {
    id: "sticker-burst",
    label: "Burst",
    tags: ["burst", "energy"],
    render: { kind: "procedural", shape: "burst", colorTag: "amber" },
    defaultScaleMilli: 1200,
    defaultRotationMilli: 15000,
    presentationReachMilli: 0,
    productionMilli: 28,
  },
  {
    id: "sticker-star",
    label: "Star",
    tags: ["star", "cute"],
    render: { kind: "procedural", shape: "star", colorTag: "gold" },
    defaultScaleMilli: 950,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 20,
  },
  {
    id: "sticker-frame",
    label: "Focus frame",
    tags: ["frame", "focus"],
    render: { kind: "procedural", shape: "frame", colorTag: "white" },
    defaultScaleMilli: 1400,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-dot",
    label: "Spot",
    tags: ["dot", "mark"],
    render: { kind: "procedural", shape: "dot", colorTag: "coral" },
    defaultScaleMilli: 700,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 12,
  },
  {
    id: "sticker-underline",
    label: "Underline",
    tags: ["underline", "emphasis"],
    render: { kind: "procedural", shape: "underline", colorTag: "cyan" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 16,
  },
  {
    id: "sticker-fire",
    label: "Fire",
    tags: ["emoji", "hype"],
    render: { kind: "procedural", shape: "text", text: "🔥", colorTag: "coral" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-skull",
    label: "Dead",
    tags: ["emoji", "funny"],
    render: { kind: "procedural", shape: "text", text: "💀", colorTag: "white" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-eyes",
    label: "Eyes",
    tags: ["emoji", "suspense"],
    render: { kind: "procedural", shape: "text", text: "👀", colorTag: "cyan" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-hundred",
    label: "100",
    tags: ["emoji", "hype"],
    render: { kind: "procedural", shape: "text", text: "💯", colorTag: "coral" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-sob",
    label: "Sob",
    tags: ["emoji", "drama"],
    render: { kind: "procedural", shape: "text", text: "😭", colorTag: "cyan" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-heart",
    label: "Heart",
    tags: ["emoji", "cute"],
    render: { kind: "procedural", shape: "text", text: "❤️", colorTag: "coral" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-clown",
    label: "Clown",
    tags: ["emoji", "funny"],
    render: { kind: "procedural", shape: "text", text: "🤡", colorTag: "amber" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-alien",
    label: "Alien",
    tags: ["emoji", "wacky"],
    render: { kind: "procedural", shape: "text", text: "👽", colorTag: "cyan" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  // ── Wearable prop stickers (worn ON the subject / cat) ──────────────────────
  // Reuse the existing procedural `text` sticker (emoji glyph). The "on-subject"
  // tag makes the shell default-anchor them over the cat's head/face
  // (see creative-defaults.ts) instead of the top-right corner.
  {
    id: "sticker-sunglasses",
    label: "Shades",
    tags: ["emoji", "prop", "on-subject", "cool"],
    render: { kind: "procedural", shape: "text", text: "🕶️", colorTag: "white" },
    defaultScaleMilli: 1200,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 20,
  },
  {
    id: "sticker-tophat",
    label: "Top hat",
    tags: ["emoji", "prop", "on-subject", "fancy"],
    render: { kind: "procedural", shape: "text", text: "🎩", colorTag: "white" },
    defaultScaleMilli: 1200,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 20,
  },
  {
    id: "sticker-partyhat",
    label: "Party",
    tags: ["emoji", "prop", "on-subject", "fun"],
    render: { kind: "procedural", shape: "text", text: "🥳", colorTag: "amber" },
    defaultScaleMilli: 1200,
    // Jaunty tilt so the party hat sits at a fun angle (millidegrees, must be >= 0).
    defaultRotationMilli: 15000,
    presentationReachMilli: 0,
    productionMilli: 20,
  },
  {
    id: "sticker-crown",
    label: "Crown",
    tags: ["emoji", "prop", "on-subject", "royal"],
    render: { kind: "procedural", shape: "text", text: "👑", colorTag: "gold" },
    defaultScaleMilli: 1200,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 20,
  },
  // ── Fluent Emoji art set additions (Task 3) ──────────────────────────────
  // Catalog `render` stays procedural (vector fallback); the shell overrides
  // with curated art by id via `STICKER_ART` (shell/lib/sticker-art.ts).
  {
    id: "sticker-shock",
    label: "Shock",
    tags: ["emoji", "reaction"],
    render: { kind: "procedural", shape: "text", text: "😮", colorTag: "cyan" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-joy",
    label: "Joy",
    tags: ["emoji", "funny"],
    render: { kind: "procedural", shape: "text", text: "😂", colorTag: "amber" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-siren",
    label: "Siren",
    tags: ["emoji", "alarm"],
    render: { kind: "procedural", shape: "text", text: "🚨", colorTag: "coral" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-speech",
    label: "Speech",
    tags: ["emoji", "talk"],
    render: { kind: "procedural", shape: "text", text: "💬", colorTag: "white" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-ring",
    label: "Ring",
    tags: ["functional", "focus"],
    render: { kind: "procedural", shape: "frame", colorTag: "gold" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sticker-censor",
    label: "Censor",
    tags: ["functional", "hide"],
    render: { kind: "procedural", shape: "underline", colorTag: "white" },
    defaultScaleMilli: 1000,
    defaultRotationMilli: 0,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
];

/**
 * Cosmetic caption reaction-tag glyphs (shell-only decoration).
 * Rendered UNDER the caption headline in the live overlay + Caption tool preview.
 * These never enter the SourcePack / scoring graph — `tone` maps each reaction to
 * an existing expressive tag family (reused pattern) for styling only, so
 * `validateSourcePack` is unaffected.
 */
export interface ReactionTag {
  id: string;
  label: string;
  emoji: string;
  /** Cosmetic tone reusing existing expressive tag families (no scoring impact). */
  tone: "neutral" | "hype" | "curious" | "skeptical" | "sad" | "urgent";
}

export const SHARED_REACTION_TAGS: ReactionTag[] = [
  { id: "reaction-none", label: "None", emoji: "🚫", tone: "neutral" },
  { id: "reaction-shock", label: "Shocked", emoji: "😮", tone: "curious" },
  { id: "reaction-cry", label: "Crying", emoji: "😢", tone: "sad" },
  { id: "reaction-angry", label: "Angry", emoji: "😡", tone: "urgent" },
  { id: "reaction-love", label: "Love", emoji: "😍", tone: "hype" },
  { id: "reaction-fire", label: "Fire", emoji: "🔥", tone: "hype" },
  { id: "reaction-skull", label: "Dead", emoji: "💀", tone: "skeptical" },
  { id: "reaction-clown", label: "Clown", emoji: "🤡", tone: "skeptical" },
  { id: "reaction-alien", label: "Alien", emoji: "👽", tone: "curious" },
  { id: "reaction-flag", label: "Red flag", emoji: "🚩", tone: "urgent" },
  { id: "reaction-mindblown", label: "Mind blown", emoji: "🤯", tone: "curious" },
  { id: "reaction-scream", label: "Scream", emoji: "😱", tone: "urgent" },
  { id: "reaction-plead", label: "Pleading", emoji: "🥺", tone: "sad" },
  { id: "reaction-flushed", label: "Flushed", emoji: "😳", tone: "curious" },
  { id: "reaction-eyeroll", label: "Eye roll", emoji: "🙄", tone: "skeptical" },
  { id: "reaction-heart", label: "Heart", emoji: "❤️", tone: "hype" },
];

/** Authored SFX catalog — each option maps to a unique production audio asset. */
export const SHARED_SFX_OPTIONS: SfxOption[] = [
  {
    id: "sfx-opt-whoosh",
    label: "Whoosh",
    tags: ["whoosh", "motion"],
    assetId: "sfx-whoosh",
    defaultAtTick: 0,
    defaultDurationTicks: 12,
    defaultVolumeMilli: 700,
    presentationReachMilli: 0,
    productionMilli: 20,
  },
  {
    id: "sfx-opt-impact",
    label: "Impact",
    tags: ["impact", "hit"],
    assetId: "sfx-impact",
    defaultAtTick: 0,
    defaultDurationTicks: 10,
    defaultVolumeMilli: 800,
    presentationReachMilli: 0,
    productionMilli: 25,
  },
  {
    id: "sfx-opt-notify-positive",
    label: "Ping",
    tags: ["notify", "positive"],
    assetId: "sfx-notify-positive",
    defaultAtTick: 0,
    defaultDurationTicks: 8,
    defaultVolumeMilli: 650,
    presentationReachMilli: 0,
    productionMilli: 18,
  },
  {
    id: "sfx-opt-notify-alert",
    label: "Alert",
    tags: ["notify", "alert", "ominous", "alarm"],
    assetId: "sfx-notify-alert",
    defaultAtTick: 0,
    defaultDurationTicks: 10,
    defaultVolumeMilli: 750,
    presentationReachMilli: 0,
    productionMilli: 22,
  },
  {
    id: "sfx-opt-glitch",
    label: "Glitch",
    tags: ["glitch", "digital"],
    assetId: "sfx-glitch",
    defaultAtTick: 0,
    defaultDurationTicks: 8,
    defaultVolumeMilli: 600,
    presentationReachMilli: 0,
    productionMilli: 24,
  },
  {
    id: "sfx-opt-success",
    label: "Success",
    tags: ["success", "positive"],
    assetId: "sfx-success",
    defaultAtTick: 0,
    defaultDurationTicks: 12,
    defaultVolumeMilli: 700,
    presentationReachMilli: 0,
    productionMilli: 20,
  },
];

export function sharedCreativeCatalogFields(stickerOptions: StickerOption[] = SHARED_STICKER_OPTIONS) {
  return {
    lookTreatments: SHARED_LOOK_TREATMENTS,
    cameraMotions: SHARED_CAMERA_MOTIONS,
    transitionPresets: SHARED_TRANSITION_PRESETS,
    stickerOptions,
    sfxOptions: SHARED_SFX_OPTIONS,
  };
}

/** Focus/arrow stickers constrained to early story beats per pack. */
export function stickersForEarlyBeats(earlyBeatIds: string[]): StickerOption[] {
  return SHARED_STICKER_OPTIONS.map((sticker) => {
    if (sticker.id === "sticker-arrow" || sticker.id === "sticker-frame") {
      return { ...sticker, allowedBeatIds: [...earlyBeatIds] };
    }
    return sticker;
  });
}

/**
 * A HEADLINE FRAMING: one full preset headline that spins the SAME footage a
 * particular way. The Caption tool shows these as tappable cards (replacing the
 * old word-by-word tile builder). `tokenIds` are existing, grammar-valid pack
 * caption tokens, emitted through the normal `text` edit op — no new core op.
 *
 * `risk` is the lesson (omission vs fabrication), shown as a colored cue:
 *   • "deniable"  — emotional spin, hard to fact-check, low exposure (green).
 *   • "checkable" — a factual assertion that leaves receipts, high cred risk (red).
 */
/**
 * A Caption tool framing card. The DATA is authored per level in its
 * level.json (`headlineFramings`); this is only the shape it is read into.
 */
export interface HeadlineFraming {
  id: string;
  /** Full headline text shown on the card (all-caps reel styling). */
  text: string;
  /** One-line description of how this framing spins the footage. */
  spin: string;
  /** Grammar-valid pack caption tokens this framing emits. */
  tokenIds: string[];
  risk: "deniable" | "checkable";
}




export const EMPTY_BOWL_HEADLINE_TEMPLATES: HeadlineTemplate[] = [
  {
    id: "hl-bowl-hungry",
    label: "Left him hungry",
    segments: [
      { kind: "fixed", id: "seg-left-him", tokenIds: ["tok-left", "tok-him"] },
      {
        kind: "slot",
        id: "slot-how",
        defaultOptionId: "opt-hungry",
        options: [
          { id: "opt-hungry", tokenIds: ["tok-hungry"] },
          { id: "opt-hours", tokenIds: ["tok-for-hours"] },
        ],
      },
    ],
  },
  {
    id: "hl-bowl-took",
    label: "Took the food?",
    segments: [
      {
        kind: "slot",
        id: "slot-hook",
        defaultOptionId: "opt-took",
        options: [
          { id: "opt-took", tokenIds: ["tok-took-food"] },
          { id: "opt-purpose", tokenIds: ["tok-on-purpose"] },
          { id: "opt-refill", tokenIds: ["tok-refill"] },
        ],
      },
    ],
  },
  {
    id: "hl-bowl-label",
    label: "Empty bowl",
    segments: [
      {
        kind: "slot",
        id: "slot-label",
        defaultOptionId: "opt-empty",
        options: [
          { id: "opt-empty", tokenIds: ["tok-empty-bowl"] },
          { id: "opt-hungry-cat", tokenIds: ["tok-hungry-cat"] },
        ],
      },
    ],
  },
];



export const FOUND_PHONE_HEADLINE_TEMPLATES: HeadlineTemplate[] = [
  {
    id: "hl-phone-stole",
    label: "They stole it",
    segments: [
      { kind: "fixed", id: "seg-they", tokenIds: ["tok-they"] },
      {
        kind: "slot",
        id: "slot-action",
        defaultOptionId: "opt-stole",
        options: [
          { id: "opt-stole", tokenIds: ["tok-stole"] },
          { id: "opt-grabbed", tokenIds: ["tok-grabbed"] },
        ],
      },
      { kind: "fixed", id: "seg-it", tokenIds: ["tok-it"] },
    ],
  },
  {
    id: "hl-phone-snatched",
    label: "Snatched?",
    segments: [
      {
        kind: "slot",
        id: "slot-hook",
        defaultOptionId: "opt-snatched",
        options: [
          { id: "opt-snatched", tokenIds: ["tok-snatched"] },
          { id: "opt-took", tokenIds: ["tok-took-it"] },
        ],
      },
    ],
  },
  {
    id: "hl-phone-found",
    label: "Found phone",
    segments: [
      {
        kind: "slot",
        id: "slot-hook",
        defaultOptionId: "opt-found",
        options: [
          { id: "opt-found", tokenIds: ["tok-found-phone"] },
          { id: "opt-returned", tokenIds: ["tok-returned"] },
        ],
      },
    ],
  },
  {
    id: "hl-phone-label",
    label: "Who?",
    segments: [
      {
        kind: "slot",
        id: "slot-who",
        defaultOptionId: "opt-finder",
        options: [
          { id: "opt-finder", tokenIds: ["tok-finder"] },
          { id: "opt-they", tokenIds: ["tok-they"] },
        ],
      },
    ],
  },
];
